import {
  DATE_FORMAT,
  DATE_PART_WIDTH,
  TYPED_SEPARATORS,
  formatIsoDate,
  parseCompleteTypedDate,
} from './isoDate';

/** One change to a date field's text, as its input event reports it. */
export interface TypedDateEdit {
  /** The text the field showed before the edit. */
  previous: string;
  /** The text after the browser applied the keystroke, paste or deletion. */
  next: string;
  /** Where the browser left the caret in `next` (`selectionStart`). */
  caret: number;
  /** The input event's `inputType` — tells a Delete from a Backspace. */
  inputType?: string;
}

const SEPARATOR = DATE_FORMAT.separator;
const WIDTHS = DATE_FORMAT.order.map((part) => DATE_PART_WIDTH[part]);

const isDigit = (char: string) => char >= '0' && char <= '9';
const isSeparator = (char: string) => TYPED_SEPARATORS.includes(char);

/**
 * The parts of text the mask could have written itself — `01.01.20`, `01.`, `0`, `01` —
 * or `null` for anything else (a paste the parser refused, an edit made mid-text).
 */
function readParts(text: string): string[] | null {
  const parts = text.split(SEPARATOR);
  if (parts.length > WIDTHS.length) return null;
  const last = parts.length - 1;
  const readable = parts.every(
    (part, index) =>
      /^\d*$/.test(part) &&
      (index < last ? part.length === WIDTHS[index] : part.length <= WIDTHS[index]),
  );
  return readable ? parts : null;
}

/** One character typed at the end of readable text. */
function typeAtEnd(parts: string[], char: string): string[] {
  const next = [...parts];
  const open = next.length - 1;
  const hasFollower = open < WIDTHS.length - 1;
  if (isDigit(char)) {
    if (next[open].length < WIDTHS[open]) next[open] += char;
    else if (hasFollower) next.push(char);
    // The year is full: a further digit has nowhere to go.
    else return next;
    // A full part is closed at once — its separator appears with its last digit.
    const last = next.length - 1;
    if (next[last].length === WIDTHS[last] && last < WIDTHS.length - 1) next.push('');
    return next;
  }
  // A typed separator closes the part being typed, padding a lone digit: `7.` → `07.`.
  // Where nothing is open to close (`01.` + `.`), in the year, or after a lone `0` (no
  // day or month is 00), it is swallowed. Any other character is dropped.
  if (isSeparator(char) && hasFollower && next[open] !== '' && next[open] !== '0') {
    next[open] = next[open].padStart(WIDTHS[open], '0');
    next.push('');
  }
  return next;
}

/**
 * THE FIELD WRITES ITS SEPARATORS AS THE DIGITS ARRIVE, in `DATE_FORMAT`: `0` → `01.` →
 * `01.0` → `01.01.` … `01.01.2024`, so the auditor need never type one. What an edit becomes:
 *
 * - **Typing at the end** of text the mask can read: a digit fills the part being typed, and
 *   a full part is followed at once by the separator; a digit past the full year is dropped.
 *   A typed separator (any of `TYPED_SEPARATORS`) closes the part — `7.` pads to `07.`, so
 *   `7.10.2026` still reads `07.10.2026` — and is swallowed where the mask already put one
 *   (no `01..`). Letters are dropped.
 * - **Backspace at the end** that took only a separator takes the digit before it too:
 *   `01.` → `0`. The separator is the mask's, not the auditor's, so Backspace removes a
 *   digit and the next digit cannot put the deleted separator straight back.
 * - **More than one character at once** (a paste, a drop, autofill) is read by the parser
 *   first: a complete day — `01.01.2024`, `01/01/2024`, `2024-01-01`, `01012024` — is shown
 *   formatted. Anything else stays exactly as it came; it is never regrouped digit by digit
 *   (`2024-01-01` must not become `20.24.0101`), and a commit turns it red if it names no day.
 *   A two-digit year waits for the commit too: a pasted `20` after `01.01.` is not 2020 yet.
 *   An ISO date is a paste shape: typed key by key, its digits fill the format like any others.
 * - **Anything else stays as typed** — an edit before the end of the text, or on text the
 *   mask cannot read. Re-grouping digits mid-text would shift them across the separators
 *   and move the caret under the auditor's hands; left alone, the browser keeps the caret
 *   where it was, and the parser — which reads `1.2.2024` and `01.1.2024` too — takes the
 *   text on commit.
 *
 * Pure: the field hands in the edit and shows what comes back.
 */
export function maskTypedDate({ previous, next, caret, inputType }: TypedDateEdit): string {
  // Where the edit happened: the text after the caret is what it left alone.
  const after = next.slice(caret);
  const tail = previous.endsWith(after) ? after : '';
  const head = next.slice(0, next.length - tail.length);
  const previousHead = previous.slice(0, previous.length - tail.length);
  let kept = 0;
  while (kept < head.length && head[kept] === previousHead[kept]) kept += 1;
  const inserted = head.slice(kept);
  const removed = previousHead.slice(kept);

  if (inserted.length > 1) {
    const day = parseCompleteTypedDate(next);
    return day ? formatIsoDate(day) : next;
  }
  if (tail) return next;

  if (inserted) {
    const parts = readParts(head.slice(0, kept));
    return parts ? typeAtEnd(parts, inserted).join(SEPARATOR) : next;
  }
  // A Backspace — or a whole-value change, which reports no input type. Not a Delete, and
  // not a digit typed over a selected `1.` that happens to leave `01`: those keep `next`.
  const backspace =
    !inputType || (inputType.startsWith('delete') && !inputType.includes('Forward'));
  const tookOnlySeparators = removed !== '' && removed.split('').every(isSeparator);
  if (backspace && tookOnlySeparators && readParts(previous)) return head.slice(0, -1);
  return next;
}
